import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useParams, useNavigate } from 'react-router-dom';
import { MathJax } from 'better-react-mathjax';
import { 
  Clock, CheckSquare, List, Edit, Save, Plus, Trash2, Info, 
  Target, ChevronRight, Lightbulb, ArrowRight, BookOpen, Zap
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { askGPT } from '../components/OpenAIService';

const PlanAction = ({ 
  userId, 
  competence: initialCompetence,  // NOUVEAU : Recevoir la compétence en prop
  onPlanUpdate, 
  startInEditMode = false 
}) => {
  const { competenceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false); // Pas de loading initial si competence est fournie
  const [error, setError] = useState(null);
  const [competence, setCompetence] = useState(initialCompetence || null);
  const [actionPlan, setActionPlan] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // État pour le mode édition
  const [isEditing, setIsEditing] = useState(startInEditMode);
  
  const [editableSteps, setEditableSteps] = useState([]);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  
  console.log('=== PLANACTION DEBUT ===');
  console.log('competenceId:', competenceId);
  console.log('userId:', userId);
  console.log('initialCompetence:', !!initialCompetence);
  console.log('competence actuelle:', !!competence);
  
  // Définition des styles de police
  const fontStyles = `
    .page-title {
      font-size: 1.75rem;
      line-height: 2.25rem;
      font-weight: 700;
    }
    .section-title {
      font-size: 1.5rem;
      line-height: 2rem;
      font-weight: 600;
    }
    .main-text {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
    .objective-text {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
    textarea.input-text {
      font-size: 1.125rem !important;
    }
    .button-text {
      font-size: 1rem;
    }
  `;
  
  // Charger la compétence SEULEMENT si elle n'est pas fournie en prop
  useEffect(() => {
    const fetchCompetence = async () => {
      console.log('fetchCompetence appelé, initialCompetence:', !!initialCompetence);
      
      // Si on a déjà la compétence en prop, on l'utilise
      if (initialCompetence) {
        console.log('Utilisation de la compétence fournie en prop');
        setCompetence(initialCompetence);
        if (initialCompetence.actionPlan) {
          setActionPlan(initialCompetence.actionPlan);
          setEditableSteps(initialCompetence.actionPlan.steps || []);
        }
        return;
      }
      
      // Sinon, on charge depuis Firebase (fallback)
      if (!competenceId || !userId) {
        setError('Identifiant de compétence ou utilisateur non défini');
        setLoading(false);
        return;
      }

      try {
        console.log('Chargement depuis Firebase...');
        setLoading(true);
        const docSnap = await getDoc(doc(db, 'users', userId, 'competences', competenceId));
        
        if (docSnap.exists()) {
          const competenceData = { ...docSnap.data(), id: competenceId };
          console.log('Compétence chargée depuis Firebase:', competenceData.title);
          setCompetence(competenceData);
          
          if (competenceData.actionPlan) {
            setActionPlan(competenceData.actionPlan);
            setEditableSteps(competenceData.actionPlan.steps || []);
          }
        } else {
          setError('Aucune compétence trouvée.');
        }
      } catch (e) {
        console.error('Erreur de chargement:', e);
        setError('Erreur lors de la récupération de la compétence.');
      } finally {
        setLoading(false);
      }
    };

    fetchCompetence();
  }, [competenceId, userId, initialCompetence]);
  
  // Générer un plan d'action initial basé sur les recommandations
  const generateInitialPlan = async () => {
    console.log('generateInitialPlan appelé');
    console.log('competence:', !!competence);
    console.log('recommendation:', !!competence?.recommendation);
    console.log('title:', competence?.title);
    
    if (!competence || !competence.recommendation || !competence.title) {
      console.log('Informations insuffisantes pour générer un plan');
      setError('Informations insuffisantes pour générer un plan d\'action. Assurez-vous d\'avoir des recommandations.');
      return;
    }
    
    setIsGenerating(true);
    
    try {
      const prompt = `En tant que coach spécialisé en développement personnel et professionnel, crée un plan d'action concret basé sur l'objectif et les recommandations suivantes.

Objectif de l'utilisateur : "${competence.title}"

Recommandations précédentes : "${competence.recommendation}"

Génère un plan d'action avec 3 à 5 étapes claires et actionnables sous format JSON avec cette structure précise :
{
  "titre": "Plan d'action pour [résumé de l'objectif]",
  "description": "Une description brève du plan d'action qui explique comment il va aider l'utilisateur à atteindre son objectif",
  "steps": [
    {
      "id": 1,
      "titre": "Titre de l'étape 1",
      "description": "Description détaillée de l'étape 1 avec des actions concrètes",
      "duree": "Estimation de la durée (ex: 2 semaines)",
      "termine": false
    },
    ...et ainsi de suite pour chaque étape
  ]
}

Assure-toi que:
- Chaque étape soit spécifique, mesurable et réalisable
- Le format JSON soit strictement respecté pour faciliter le traitement
- Le contenu utilise des verbes d'action et encourage l'utilisateur
- Les étapes suivent une progression logique vers l'objectif final`;

      const response = await askGPT(prompt);
      
      // Extraction du JSON de la réponse
      let planData;
      try {
        const jsonMatch = response.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          planData = JSON.parse(jsonMatch[0]);
        } else {
          throw new Error('Format JSON non détecté dans la réponse');
        }
      } catch (jsonError) {
        console.error('Erreur de parsing JSON:', jsonError);
        throw new Error('Impossible de parser le plan d\'action');
      }
      
      // Mise à jour en base de données
      const docRef = doc(db, 'users', userId, 'competences', competenceId);
      await updateDoc(docRef, {
        actionPlan: planData,
        actionPlanCreatedAt: new Date().toISOString()
      });
      
      // Mise à jour de l'état
      setActionPlan(planData);
      setEditableSteps(planData.steps || []);
      setFeedbackMessage('Plan d\'action généré avec succès !');
      
      // Notifier le composant parent
      if (onPlanUpdate) onPlanUpdate();
      
      setTimeout(() => {
        setFeedbackMessage('');
      }, 3000);
      
    } catch (err) {
      console.error('Erreur lors de la génération du plan:', err);
      setError('Erreur lors de la génération du plan d\'action');
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Ajouter une nouvelle étape
  const addNewStep = () => {
    const newStep = {
      id: editableSteps.length > 0 ? Math.max(...editableSteps.map(step => step.id)) + 1 : 1,
      titre: "Nouvelle étape",
      description: "Description de l'étape",
      duree: "À déterminer",
      termine: false
    };
    
    setEditableSteps([...editableSteps, newStep]);
  };
  
  // Supprimer une étape
  const removeStep = (id) => {
    setEditableSteps(editableSteps.filter(step => step.id !== id));
  };
  
  // Mettre à jour une étape
  const updateStep = (id, field, value) => {
    setEditableSteps(editableSteps.map(step => 
      step.id === id ? { ...step, [field]: value } : step
    ));
  };
  
  // Sauvegarder les modifications
  const saveChanges = async () => {
    try {
      if (!actionPlan) return;
      
      const updatedPlan = {
        ...actionPlan,
        steps: editableSteps
      };
      
      const docRef = doc(db, 'users', userId, 'competences', competenceId);
      await updateDoc(docRef, {
        actionPlan: updatedPlan,
        actionPlanUpdatedAt: new Date().toISOString()
      });
      
      setActionPlan(updatedPlan);
      setIsEditing(false);
      setFeedbackMessage('Plan d\'action sauvegardé !');
      
      // Notifier le composant parent
      if (onPlanUpdate) onPlanUpdate();
      
      setTimeout(() => {
        setFeedbackMessage('');
      }, 3000);
      
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      setError('Erreur lors de la sauvegarde du plan d\'action');
    }
  };
  
  // Marquer une étape comme terminée
  const toggleStepCompletion = async (id) => {
    try {
      const updatedSteps = actionPlan.steps.map(step => 
        step.id === id ? { ...step, termine: !step.termine } : step
      );
      
      const updatedPlan = {
        ...actionPlan,
        steps: updatedSteps
      };
      
      const docRef = doc(db, 'users', userId, 'competences', competenceId);
      await updateDoc(docRef, {
        actionPlan: updatedPlan,
        actionPlanUpdatedAt: new Date().toISOString()
      });
      
      setActionPlan(updatedPlan);
      setEditableSteps(updatedSteps);
      
      // Notifier le composant parent
      if (onPlanUpdate) onPlanUpdate();
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      setError('Erreur lors de la mise à jour du statut');
    }
  };
  
  // Modifier le titre ou la description du plan
  const updatePlanDetails = (field, value) => {
    setActionPlan(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  console.log('État final:', {
    loading,
    error,
    competence: !!competence,
    actionPlan: !!actionPlan,
    competenceTitle: competence?.title
  });
  
  // Affichage pendant le chargement
  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        <p className="ml-3">Chargement du plan d'action...</p>
      </div>
    );
  }
  
  // Affichage en cas d'erreur
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
        <p className="font-semibold">Une erreur est survenue</p>
        <p>{error}</p>
        <div className="mt-2 text-sm">
          <p>Debug info:</p>
          <p>competenceId: {competenceId}</p>
          <p>userId: {userId}</p>
          <p>competence fournie: {initialCompetence ? 'Oui' : 'Non'}</p>
          <p>competence chargée: {competence ? 'Oui' : 'Non'}</p>
        </div>
      </div>
    );
  }

  // Affichage si pas de compétence
  if (!competence) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 p-4 rounded-lg">
        <p>Aucune compétence disponible.</p>
        <div className="mt-2 text-sm">
          <p>competenceId: {competenceId}</p>
          <p>userId: {userId}</p>
          <p>initialCompetence: {initialCompetence ? 'Fournie' : 'Non fournie'}</p>
        </div>
      </div>
    );
  }

  // Rendu du composant
  return (
    <div className="w-full">
      {/* Injecter les styles de police */}
      <style>{fontStyles}</style>
      
      {/* En-tête du plan d'action */}
      <div className="w-full bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow p-6 mb-6">
        <div className="flex items-center mb-4">
          <Zap className="h-6 w-6 text-orange-600 mr-2" />
          <h2 className="text-xl font-semibold text-gray-800">Plan d'actions pour réaliser mon objectif</h2>
        </div>
        
        <div className="bg-white bg-opacity-70 p-4 rounded-lg border border-blue-100">
          <p className="text-gray-800">{competence?.title}</p>
        </div>
      </div>

      <div className="w-full bg-white rounded-xl shadow-lg border border-gray-200 p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center">
            <List className="w-6 h-6 mr-2 text-indigo-600" />
            {isEditing ? "Modification du Plan d'Action" : "Aperçu du Plan d'Action"}
          </h2>
          
          {actionPlan && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 rounded-lg bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50 flex items-center gap-2 transition-all duration-200 shadow-sm"
            >
              <Edit className="w-4 h-4" />
              <span>Modifier les détails</span>
            </button>
          )}
        </div>
        
        {feedbackMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-lg mb-6 flex items-center">
            <CheckSquare className="w-5 h-5 mr-2" />
            <p>{feedbackMessage}</p>
          </div>
        )}
        
        {!actionPlan && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 text-center mb-6">
            <Lightbulb className="w-12 h-12 text-indigo-400 mx-auto mb-4" />
            <p className="text-gray-700 mb-6 main-text">
              Générez un plan d'action personnalisé basé sur les recommandations pour concrétiser votre objectif.
            </p>
            <button
              onClick={generateInitialPlan}
              disabled={isGenerating}
              className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-200 mx-auto
                ${isGenerating 
                  ? 'bg-gray-300 cursor-not-allowed text-gray-500' 
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-md hover:shadow-lg'}`}
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  <span>Génération en cours...</span>
                </>
              ) : (
                <>
                  <Lightbulb className="w-5 h-5" />
                  <span>Générer un plan d'action</span>
                </>
              )}
            </button>
          </div>
        )}
        
        {actionPlan && (
          <>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 shadow-sm border border-indigo-100 mb-8">
              {isEditing ? (
                <>
                  <input
                    type="text"
                    value={actionPlan.titre}
                    onChange={(e) => updatePlanDetails('titre', e.target.value)}
                    className="w-full mb-4 p-2 text-xl font-semibold border-b-2 border-indigo-200 focus:border-indigo-500 focus:outline-none bg-transparent"
                  />
                  <textarea
                    value={actionPlan.description}
                    onChange={(e) => updatePlanDetails('description', e.target.value)}
                    className="w-full p-2 border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-h-24 bg-white"
                  />
                </>
              ) : (
                <>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">{actionPlan.titre}</h3>
                  <div className="prose max-w-none text-gray-700">
                    <MathJax>
                      <ReactMarkdown>{actionPlan.description}</ReactMarkdown>
                    </MathJax>
                  </div>
                </>
              )}
            </div>
            
            <div className="space-y-6">
              {(isEditing ? editableSteps : actionPlan.steps).map((step, index) => (
                <div key={step.id} className={`bg-white rounded-lg p-6 shadow-sm border ${step.termine ? 'border-green-200 bg-green-50' : 'border-indigo-100'}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center">
                      {!isEditing && (
                        <div 
                          onClick={() => toggleStepCompletion(step.id)}
                          className={`w-6 h-6 rounded-full mr-3 flex items-center justify-center cursor-pointer ${step.termine ? 'bg-green-500 text-white' : 'border-2 border-indigo-300 hover:border-indigo-500'}`}
                        >
                          {step.termine && <CheckSquare className="w-5 h-5" />}
                          {!step.termine && <span className="text-indigo-500 font-medium">{step.id}</span>}
                        </div>
                      )}
                      {isEditing ? (
                        <input
                          type="text"
                          value={step.titre}
                          onChange={(e) => updateStep(step.id, 'titre', e.target.value)}
                          className="text-lg font-medium text-gray-800 p-2 border-b-2 border-indigo-200 focus:border-indigo-500 focus:outline-none bg-transparent w-full"
                        />
                      ) : (
                        <h4 className={`text-lg font-medium ${step.termine ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
                          {step.titre}
                        </h4>
                      )}
                    </div>
                    
                    {isEditing && (
                      <button 
                        onClick={() => removeStep(step.id)}
                        className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  
                  <div className="ml-9">
                    {isEditing ? (
                      <textarea
                        value={step.description}
                        onChange={(e) => updateStep(step.id, 'description', e.target.value)}
                        className="w-full p-2 border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-h-24 mb-4 bg-white"
                      />
                    ) : (
                      <div className={`prose max-w-none mb-4 ${step.termine ? 'text-gray-500' : 'text-gray-700'}`}>
                        <MathJax>
                          <ReactMarkdown>{step.description}</ReactMarkdown>
                        </MathJax>
                      </div>
                    )}
                    
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="w-4 h-4 mr-1" />
                      {isEditing ? (
                        <input
                          type="text"
                          value={step.duree}
                          onChange={(e) => updateStep(step.id, 'duree', e.target.value)}
                          className="p-1 border border-indigo-200 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                        />
                      ) : (
                        <span>{step.duree}</span>
                      )}
                    </div>
                    
                    {!isEditing && !step.termine && (
                      <div className="mt-4">
                        <button
                          onClick={() => toggleStepCompletion(step.id)}
                          className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1 text-sm bg-indigo-50 px-3 py-1 rounded-lg border border-indigo-100 transition-colors"
                        >
                          Marquer comme terminé <CheckSquare size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {isEditing && (
                <button
                  onClick={addNewStep}
                  className="w-full py-3 border-2 border-dashed border-indigo-300 rounded-lg text-indigo-600 hover:bg-indigo-50 flex items-center justify-center gap-2 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  <span>Ajouter une étape</span>
                </button>
              )}
            </div>
            
            {isEditing && (
              <div className="flex justify-end mt-6">
                <button
                  onClick={saveChanges}
                  className="px-6 py-3 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white flex items-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  <Save className="w-5 h-5" />
                  <span>Enregistrer les modifications</span>
                </button>
              </div>
            )}
            
            {/* Liens rapides vers les autres espaces */}
            {!isEditing && (
              <div className="flex justify-center mt-10 space-x-4">
                <button
                  onClick={() => navigate(`/cours/${competenceId}`)}
                  className="px-4 py-2 rounded-lg flex items-center gap-2 transition-colors bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>Espace exploration</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => navigate(`/inspiration/${competenceId}`)}
                  className="px-4 py-2 rounded-lg flex items-center gap-2 transition-colors bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100"
                >
                  <Zap className="w-4 h-4" />
                  <span>Espace inspiration</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PlanAction;